import React, { useState, useEffect, useRef } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	Chip,
	Paper,
	IconButton,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Slide,
	alpha,
	Stack,
	CircularProgress,
	Card,
	CardContent,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	useTheme,
	TextField,
	Grid,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import {
	Close,
	Phone,
	Person,
	CalendarToday,
	AccountCircle,
	WhatsApp,
	Comment,
	Assignment,
	AutoAwesome,
	ExpandMore,
	Chat,
	Delete,
	CompareArrows,
	HotelClass,
	Star,
} from "@mui/icons-material";
import { Lead, User, Conversation, LeadQuality, LeadStatus, Observacao, Status } from "../types";
import { AgentUserRS } from "../utils/api";
import ObservacoesModal from "./ObservacoesModal";
import {
	WhatshotTwoTone as HotIcon,
	AcUnitTwoTone as ColdIcon,
	BalanceTwoTone as NeutralIcon,
} from "@mui/icons-material";
import {
	fetchChatHistoryByUserId,
	updateLeadQuality,
	updateLeadStatus,
	updateLeadName,
	getLeadObservacoes,
	suggestResponse,
	fetchAgentName,
	updateLeadRating,
} from "../utils/api";
import { isManagerRole, isAdminLike, isSalesRole } from "../utils/roleMapping";
import { statusApi } from "../utils/statusApi";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { yellow } from "@mui/material/colors";
import { getDocumentType } from "../utils/document";
import { agentApi } from "../utils/agentApi";
import { fetchRatingByPhone } from "../utils/ratingApi";

const Transition = React.forwardRef(function Transition(
	props: TransitionProps & {
		children: React.ReactElement<any, any>;
	},
	ref: React.Ref<unknown>
) {
	return <Slide direction="up" ref={ref} {...props} />;
});

interface LeadDetailModalProps {
	open: boolean;
	lead: Lead | null;
	users: User[];
	agentUsers?: AgentUserRS[];
	onClose: () => void;
	onUpdateLead: (updatedLead: Lead) => void;
	onAssignToMe?: (leadId: string) => void;
	onDeleteLead?: (lead: Lead) => void;
	onTransferLead?: (lead: Lead) => void;
	isMobile?: boolean;
	hideConversarButton?: boolean;
}

const LeadDetailModal: React.FC<LeadDetailModalProps> = ({
	open,
	lead,
	users,
	agentUsers = [],
	onClose,
	onUpdateLead,
	onAssignToMe,
	onDeleteLead,
	onTransferLead,
	isMobile,
	hideConversarButton = false,
}) => {
	const theme = useTheme();
	const { user } = useAuth();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [conversation, setConversation] = useState<Conversation | null>(null);
	const [leadQuality, setLeadQuality] = useState<LeadQuality>("Neutro");
	const [status, setStatus] = useState<string>("novo");
	const [statuses, setStatuses] = useState<Status[]>([]);
	const [observacoesModalOpen, setObservacoesModalOpen] = useState(false);
	const [isAIGenerating, setIsAIGenerating] = useState(false);
	const [aiSuggestion, setAiSuggestion] = useState<string | null>(null);
	const [showAISuggestion, setShowAISuggestion] = useState(false);
	const [observacoes, setObservacoes] = useState<Observacao[]>([]);
	const [isEditingName, setIsEditingName] = useState(false);
	const [editedName, setEditedName] = useState("");
	const [currentLeadName, setCurrentLeadName] = useState("");
	const [agentName, setAgentName] = useState<string | null>(null);
	const [agentNameLoading, setAgentNameLoading] = useState(false);
	const [expandRatings, setExpandRatings] = useState(false);
	const [isFetchingRating, setIsFetchingRating] = useState(false);
	const ratingProcessedRef = useRef<Set<string>>(new Set());
	const [localLead, setLocalLead] = useState<Lead | null>(null);

	// Sincroniza o lead local com o lead prop
	useEffect(() => {
		if (lead) {
			setLocalLead(lead);
			setCurrentLeadName(lead.name);
		}
	}, [lead]);

	// Fetch agent name when lead changes
	useEffect(() => {
		const loadAgentName = async () => {
			if (!lead || !lead.agentId) {
				setAgentName(null);
				return;
			}

			setAgentNameLoading(true);
			try {
				const agentInfo = await fetchAgentName(lead.agentId);
				setAgentName(agentInfo.name);
			} catch (error) {
				console.error("Failed to fetch agent name:", error);
				setAgentName(null);
			} finally {
				setAgentNameLoading(false);
			}
		};

		loadAgentName();
	}, [lead]);

	useEffect(() => {
		const checkAndFetchRating = async () => {
			if (!open || !lead || !lead.agentId || !lead.phoneNumber) {
				return;
			}

			const leadId = lead._id.$oid;

			if (isFetchingRating) {
				return;
			}

			if (ratingProcessedRef.current.has(leadId)) {
				return;
			}

			if (lead.rating?.phone) {
				ratingProcessedRef.current.add(leadId);
				return;
			}

			try {
				setIsFetchingRating(true);
				ratingProcessedRef.current.add(leadId);

				const agent = await agentApi.getAgent(lead.agentId);

				if (!agent.enableLeadRating) {
					return;
				}

				if (lead.rating?.phone) {
					return;
				}

				const rating = await fetchRatingByPhone(lead.phoneNumber);

				if (rating) {
					await updateLeadRating(leadId, rating);

					const updatedLead: Lead = {
						...(localLead || lead),
						rating,
					};
					setLocalLead(updatedLead);

					onUpdateLead(updatedLead);
				}
			} catch (error) {
				console.error("Failed to fetch or update rating:", error);
				ratingProcessedRef.current.delete(leadId);
			} finally {
				setIsFetchingRating(false);
			}
		};

		checkAndFetchRating();
	}, [open, lead?._id.$oid]);

	useEffect(() => {
		if (!open) {
			ratingProcessedRef.current.clear();
			setIsFetchingRating(false);
		}
	}, [open]);

	// Helper function to map status name to LeadStatus
	const mapStatusNameToLeadStatus = (statusName: string): LeadStatus => {
		const normalized = statusName.toLowerCase().replace(/\s+/g, "_");
		if (normalized === "novo") return "novo";
		if (
			normalized === "contato_inicial" ||
			normalized === "contato-inicial" ||
			normalized === "contatoinicial" ||
			normalized === "emandamento" ||
			normalized === "em_andamento"
		)
			return "contato_inicial";
		if (normalized === "qualificado") return "qualificado";
		if (normalized === "proposta") return "proposta";
		if (normalized === "negociacao" || normalized === "negociação") return "negociacao";
		if (normalized === "fechado" || normalized === "finalizado") return "fechado";
		if (normalized === "perdido") return "perdido";
		return "novo";
	};

	// Helper function to find status by LeadStatus
	const findStatusByLeadStatus = (leadStatus: LeadStatus): Status | undefined => {
		return statuses.find((s) => {
			const statusName = s.name.toLowerCase().replace(/\s+/g, "_");
			return (
				(statusName === "novo" && leadStatus === "novo") ||
				((statusName === "contato_inicial" ||
					statusName === "contato-inicial" ||
					statusName === "contatoinicial" ||
					statusName === "emandamento" ||
					statusName === "em_andamento") &&
					leadStatus === "contato_inicial") ||
				(statusName === "qualificado" && leadStatus === "qualificado") ||
				(statusName === "proposta" && leadStatus === "proposta") ||
				((statusName === "negociacao" || statusName === "negociação") && leadStatus === "negociacao") ||
				((statusName === "fechado" || statusName === "finalizado") && leadStatus === "fechado") ||
				(statusName === "perdido" && leadStatus === "perdido")
			);
		});
	};

	useEffect(() => {
		const loadConversation = async () => {
			if (!lead) return;
			setObservacoes(lead.observacoes || []);
			setLeadQuality(lead.leadQuality || "Neutro");

			// Find the correct status to set
			let initialStatusId: string = "novo";

			if (lead.statusId) {
				// Use the statusId directly if available
				const statusInfo = statuses.find((s) => s.id === lead.statusId);
				if (statusInfo) {
					initialStatusId = statusInfo.id;
				} else {
					// If statusId not found in loaded statuses, try to find by name
					const statusName = lead.statusName || lead.status;
					if (statusName) {
						const foundStatus = statuses.find((s) => {
							const sName = s.name.toLowerCase().replace(/\s+/g, "_");
							const lName = statusName.toLowerCase().replace(/\s+/g, "_");
							return (
								sName === lName ||
								(sName === "contato_inicial" && lName === "contato_inicial") ||
								(sName === "contato-inicial" && lName === "contato_inicial") ||
								(sName === "contatoinicial" && lName === "contato_inicial") ||
								(sName === "emandamento" && lName === "contato_inicial") ||
								(sName === "em_andamento" && lName === "contato_inicial") ||
								(sName === "negociacao" && lName === "negociacao") ||
								(sName === "negociação" && lName === "negociacao") ||
								(sName === "fechado" && lName === "fechado") ||
								(sName === "finalizado" && lName === "fechado")
							);
						});
						if (foundStatus) {
							initialStatusId = foundStatus.id;
						}
					}
				}
			} else if (lead.status) {
				// Find status by name if statusId is not available
				const statusInfo = statuses.find((s) => {
					const statusName = s.name.toLowerCase().replace(/\s+/g, "_");
					const leadStatus = lead.status!.toLowerCase().replace(/\s+/g, "_");
					return (
						statusName === leadStatus ||
						(statusName === "contato_inicial" && leadStatus === "contato_inicial") ||
						(statusName === "contato-inicial" && leadStatus === "contato_inicial") ||
						(statusName === "contatoinicial" && leadStatus === "contato_inicial") ||
						(statusName === "emandamento" && leadStatus === "contato_inicial") ||
						(statusName === "em_andamento" && leadStatus === "contato_inicial") ||
						(statusName === "negociacao" && leadStatus === "negociacao") ||
						(statusName === "negociação" && leadStatus === "negociacao") ||
						(statusName === "fechado" && leadStatus === "fechado") ||
						(statusName === "finalizado" && leadStatus === "fechado")
					);
				});
				if (statusInfo) {
					initialStatusId = statusInfo.id;
				}
			}

			setStatus(initialStatusId);

			setAiSuggestion(null);
			setShowAISuggestion(false);

			const backendHistory = await fetchChatHistoryByUserId(lead.phoneNumber);
			if (backendHistory && backendHistory.messages && backendHistory.messages.length > 0) {
				const mapped: Conversation = {
					_id: { $oid: backendHistory.id || "conv_id" },
					agentId: { $oid: backendHistory.agentId || "agent_id" },
					userId: backendHistory.userId,
					ip: "",
					impulsionador: "",
					name: backendHistory.name || lead.name,
					type: "lead",
					messages: backendHistory.messages.map((m, idx) => ({
						_id: { $oid: `${backendHistory.id || "conv"}_${idx}` },
						role: m.role === "assistant" || m.role === "user" ? m.role : "assistant",
						content: m.content,
						timestamp: { $date: (m as any).timestamp || new Date().toISOString() },
					})),
					randomFollowUp: !!backendHistory.randomFollowUp,
					pauseIA: !!backendHistory.pauseIA,
					hasAnsweredClientQuestion: true,
					answeredWorkflows: [],
					__v: 1,
				};
				setConversation(mapped);
				return;
			}

			const defaultConversation: Conversation = {
				_id: { $oid: "default_conversation_id" },
				agentId: { $oid: "default_agent_id" },
				userId: lead.phoneNumber,
				ip: "0.0.0.0",
				impulsionador: "Marina Santos",
				name: lead.name,
				type: "lead",
				messages: [],
				randomFollowUp: true,
				pauseIA: false,
				hasAnsweredClientQuestion: true,
				answeredWorkflows: [],
				__v: 1,
			};
			setConversation(defaultConversation);
		};

		loadConversation();
	}, [lead, statuses]);

	useEffect(() => {
		const loadStatuses = async () => {
			if (open) {
				try {
					const statusesData = await statusApi.getStatuses();
					setStatuses(statusesData.sort((a, b) => a.order - b.order));
				} catch (err) {
					console.error("Failed to load statuses:", err);
					// Fallback to default statuses
					setStatuses([
						{ id: "novo", name: "Novo", color: "#00f5ff", order: 1, isDefault: true },
						{ id: "contato_inicial", name: "Contato Inicial", color: "#0288d1", order: 2, isDefault: true },
						{ id: "qualificado", name: "Qualificado", color: "#388e3c", order: 3, isDefault: true },
						{ id: "proposta", name: "Proposta", color: "#fbc02d", order: 4, isDefault: true },
						{ id: "negociacao", name: "Negociação", color: "#f57c00", order: 5, isDefault: true },
						{ id: "fechado", name: "Fechado", color: "#388e3c", order: 6, isDefault: true },
						{ id: "perdido", name: "Perdido", color: "#d32f2f", order: 7, isDefault: true },
					]);
				}
			}
		};

		loadStatuses();
	}, [open]);

	const handleSaveChanges = async () => {
		if (!lead) return;

		try {
			const updates: Promise<void>[] = [];

			if (lead.leadQuality !== leadQuality) {
				updates.push(updateLeadQuality(lead._id.$oid, leadQuality));
			}
			// Check if status has changed by comparing with current statusId or status
			const currentStatusId =
				lead.statusId ||
				(lead.status
					? statuses.find((s) => {
						const statusName = s.name.toLowerCase().replace(/\s+/g, "_");
						const leadStatus = lead.status!.toLowerCase().replace(/\s+/g, "_");
						return (
							statusName === leadStatus ||
							(statusName === "contato_inicial" && leadStatus === "contato_inicial") ||
							(statusName === "contato-inicial" && leadStatus === "contato_inicial") ||
							(statusName === "contatoinicial" && leadStatus === "contato_inicial") ||
							(statusName === "emandamento" && leadStatus === "contato_inicial") ||
							(statusName === "em_andamento" && leadStatus === "contato_inicial") ||
							(statusName === "negociacao" && leadStatus === "negociacao") ||
							(statusName === "negociação" && leadStatus === "negociacao") ||
							(statusName === "fechado" && leadStatus === "fechado") ||
							(statusName === "finalizado" && leadStatus === "fechado")
						);
					})?.id
					: undefined);

			if (currentStatusId !== status) {
				// Find the status info and send the textual name to the backend (supports custom statuses)
				const statusInfo = statuses.find((s) => s.id === status);
				const statusText = statusInfo?.name || "Novo";
				updates.push(updateLeadStatus(lead._id.$oid, statusText, status));
			}

			if (updates.length > 0) {
				await Promise.all(updates);
			}

			// Find the status info for the updated lead
			const statusInfo = statuses.find((s) => s.id === status);
			const mappedLeadStatus = statusInfo ? mapStatusNameToLeadStatus(statusInfo.name) : undefined;
			const updatedLead: Lead = {
				...lead,
				leadQuality,
				status: mappedLeadStatus,
				statusId: status,
				statusName: statusInfo?.name,
				statusColor: statusInfo?.color,
			};

			onUpdateLead(updatedLead);
			handleClose();
		} catch (err) {
			console.error("Failed to save lead changes", err);
		}
	};

	const handleClose = () => {
		setAiSuggestion(null);
		setShowAISuggestion(false);
		onClose();
	};

	const handleStartConversation = () => {
		if (!conversation) return;
		try {
			// Avoid agent selection modal when coming from a deep link
			localStorage.setItem("skipAgentModal", "true");
		} catch { }
		const conversationId = conversation._id.$oid;
		navigate(`/conversations?conversationId=${encodeURIComponent(conversationId)}`);
	};

	const handleLeadUpdate = (updatedLead: Lead) => {
		onUpdateLead(updatedLead);
		setObservacoes(updatedLead.observacoes || []);
	};

	const handleCloseObservacoesModal = async () => {
		setObservacoesModalOpen(false);
		if (!lead) return;
		try {
			const obs = await getLeadObservacoes(lead._id.$oid);
			setObservacoes(obs);
			onUpdateLead({ ...lead, observacoes: obs });
		} catch {
			// silently ignore refresh error
		}
	};

	const openWhatsApp = (phoneNumber: string) => {
		window.open(`https://wa.me/${phoneNumber}`, "_blank");
	};

	const generateAISuggestion = async () => {
		if (!lead || !conversation) return;

		const conversationId = conversation._id.$oid;

		setIsAIGenerating(true);
		setShowAISuggestion(false);
		setAiSuggestion(null);

		try {
			const response = await suggestResponse(conversationId);
			const selectedSuggestion = response.suggestedResponse;

			setAiSuggestion(selectedSuggestion);
			setShowAISuggestion(true);
		} catch (error) {
			console.error(t("dashboard.leadDetails.generateSuggestionError"), error);
		} finally {
			setIsAIGenerating(false);
		}
	};

	const openWhatsAppWithMessage = (phoneNumber: string, message?: string) => {
		const encodedMessage = message ? encodeURIComponent(message) : "";
		const whatsappUrl = `https://wa.me/${phoneNumber}${message ? `?text=${encodedMessage}` : ""}`;
		window.open(whatsappUrl, "_blank");
	};

	const handleStartEditingName = () => {
		if (!lead) return;
		setEditedName(currentLeadName);
		setIsEditingName(true);
	};

	const handleCancelEditingName = () => {
		setIsEditingName(false);
		setEditedName("");
	};

	const handleSaveName = async () => {
		if (!lead || editedName.trim() === "") return;

		try {
			await updateLeadName(lead._id.$oid, editedName.trim());
			// Atualiza o estado local imediatamente
			setCurrentLeadName(editedName.trim());
			// Atualiza o lead no componente pai
			const updatedLead = { ...lead, name: editedName.trim() };
			onUpdateLead(updatedLead);
			setIsEditingName(false);
			setEditedName("");
		} catch (error) {
			console.error(t("dashboard.leadDetails.updateNameError"), error);
			// Em caso de erro, volta para o nome original
			setEditedName(currentLeadName);
			setIsEditingName(false);
		}
	};

	const handleNameKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			handleSaveName();
		} else if (event.key === "Escape") {
			handleCancelEditingName();
		}
	};

	if (!lead) return null;

	// Use localLead if available (for immediate visual updates), otherwise use lead prop
	// After the null check above, lead is guaranteed to be non-null
	const displayLead: Lead = localLead || lead;
	
	const assignedUser = users.find((u) => u.id === displayLead.assignedTo);
	const assignedAgentUser = agentUsers.find((au) => au.userId === displayLead.assignedTo);
	const assignedName = assignedUser?.name || assignedAgentUser?.userName || displayLead.assignedToName;
	const createdDate = new Date(displayLead.creationTime.$date).toLocaleString("pt-BR");

	const getStatusLabel = (status: LeadStatus) => {
		switch (status) {
			case "novo":
				return t("dashboard.leadDetails.statusLabels.new");
			case "contato_inicial":
				return t("dashboard.leadDetails.statusLabels.initialContact");
			case "qualificado":
				return t("dashboard.leadDetails.statusLabels.qualified");
			case "proposta":
				return t("dashboard.leadDetails.statusLabels.proposal");
			case "negociacao":
				return t("dashboard.leadDetails.statusLabels.negotiation");
			case "fechado":
				return t("dashboard.leadDetails.statusLabels.closed");
			case "perdido":
				return t("dashboard.leadDetails.statusLabels.lost");
			default:
				return t("dashboard.leadDetails.statusLabels.notAvailable");
		}
	};

	const getStatusColor = (status: LeadStatus) => {
		switch (status) {
			case "novo":
				return "#2196f3";
			case "contato_inicial":
				return "#ff9800";
			case "qualificado":
				return "#4caf50";
			case "proposta":
				return "#9c27b0";
			case "negociacao":
				return "#ff5722";
			case "fechado":
				return "#4caf50";
			case "perdido":
				return "#f44336";
			default:
				return "#757575";
		}
	};

	const statusColor = getStatusColor(displayLead.status || "novo");

	return (
		<>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="md"
				fullWidth
				fullScreen={isMobile}
				TransitionComponent={Transition}
				sx={{
					"& .MuiDialog-paper": {
						borderRadius: isMobile ? 0 : 2,
						margin: isMobile ? 0 : "24px",
						maxWidth: isMobile ? "100vw" : "900px",
						maxHeight: isMobile ? "100vh" : "85vh",
					},
				}}
			>
				<DialogTitle
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						background:
							theme.palette.mode === "dark"
								? "linear-gradient(135deg, #65f0c5 0%, #65f0c5 100%)"
								: "linear-gradient(135deg, #1a1a1a 0%, #34495e 100%)",
						color: "white",
						p: 2,
						minHeight: "60px",
					}}
				>
					<Box sx={{ flex: 1 }}>
						{isEditingName ? (
							<TextField
								value={editedName}
								onChange={(e) => setEditedName(e.target.value)}
								onKeyDown={handleNameKeyPress}
								onBlur={handleSaveName}
								autoFocus
								variant="standard"
								sx={{
									color: theme.palette.mode === "dark" ? "#000" : undefined,
									"& .MuiInput-underline:before": {
										borderBottomColor: "rgba(255, 255, 255, 0.7)",
									},
									"& .MuiInput-underline:after": {
										borderBottomColor: "white",
									},
									"& .MuiInputBase-input": {
										color: theme.palette.mode === "dark" ? "#000" : undefined,
										fontSize: "1.25rem",
										fontWeight: 600,
										padding: 0,
									},
								}}
							/>
						) : (
							<Typography
								variant="h6"
								component="div"
								sx={{
									fontWeight: 600,
									fontSize: "1.25rem",
									cursor: "pointer",
									minHeight: "1.5em",
									color: theme.palette.mode === "dark" ? "#000" : undefined,
									"&:hover": {
										opacity: 0.8,
									},
								}}
								onClick={handleStartEditingName}
							>
								{currentLeadName || (
									<Box component="span" sx={{ opacity: 0.6, fontStyle: "italic" }}>
										{t("dashboard.leadDetails.clickToAddName")}
									</Box>
								)}
							</Typography>
						)}
						<Typography
							variant="caption"
							sx={{ opacity: 0.9, color: theme.palette.mode === "dark" ? "#000" : undefined }}
						>
							{getStatusLabel(displayLead.status || "novo")}
						</Typography>
					</Box>
					<IconButton
						onClick={handleClose}
						sx={{
							color: theme.palette.mode === "dark" ? "#000" : "white",
							backgroundColor:
								theme.palette.mode === "dark" ? alpha("#000000", 0.2) : alpha("#ffffff", 0.2),
							"&:hover": {
								backgroundColor:
									theme.palette.mode === "dark" ? alpha("#000000", 0.3) : alpha("#ffffff", 0.3),
							},
						}}
					>
						<Close />
					</IconButton>
				</DialogTitle>

				<DialogContent sx={{ p: 2, mt: 1 }}>
					<Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
						{/* Informações Principais */}
						<Box sx={{ flex: { xs: 1, md: 2 } }}>
							<Card sx={{ height: "fit-content", border: `1px solid ${alpha(statusColor, 0.2)}` }}>
								<CardContent sx={{ p: 2 }}>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											mb: 2,
										}}
									>
										<Typography variant="subtitle1" sx={{ fontWeight: 600, color: statusColor }}>
											{t("dashboard.leadDetails.generalInfo")}
										</Typography>
										<Box sx={{ display: "flex", gap: 1 }}>
											{displayLead.leadQuality === "Quente" && (
												<HotIcon sx={{ fontSize: 20, color: "#e53935" }} />
											)}
											{displayLead.leadQuality === "Neutro" && (
												<NeutralIcon sx={{ fontSize: 20, color: "#fb8c00" }} />
											)}
											{displayLead.leadQuality === "Frio" && (
												<ColdIcon sx={{ fontSize: 20, color: "#039be5" }} />
											)}
											<Chip
												label={getStatusLabel(displayLead.status || "novo")}
												size="small"
												sx={{
													background: theme.palette.mode === "dark" ? "#313131" : "#f2f2f2",
													color: statusColor,
													fontWeight: 500,
												}}
											/>
										</Box>
									</Box>

									<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
											{displayLead.assignedTo && (
												<Box sx={{ flex: "1 1 45%", minWidth: "200px" }}>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<Phone sx={{ fontSize: 18, color: "text.secondary" }} />
														<Box>
															<Typography variant="caption" color="text.secondary">
																{t("dashboard.leadDetails.phone")}
															</Typography>
															<Typography variant="body2" sx={{ fontWeight: 500 }}>
																{displayLead.phoneNumber}
															</Typography>
														</Box>
													</Box>
												</Box>
											)}

											{assignedName && (
												<Box sx={{ flex: "1 1 45%", minWidth: "200px" }}>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<Person sx={{ fontSize: 18, color: "text.secondary" }} />
														<Box>
															<Typography variant="caption" color="text.secondary">
																{t("dashboard.leadDetails.responsible")}
															</Typography>
															<Typography variant="body2" sx={{ fontWeight: 500 }}>
																{assignedName}
															</Typography>
														</Box>
													</Box>
												</Box>
											)}

											{(agentName || agentNameLoading) && (
												<Box sx={{ flex: "1 1 45%", minWidth: "200px" }}>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<AutoAwesome sx={{ fontSize: 18, color: "text.secondary" }} />
														<Box>
															<Typography variant="caption" color="text.secondary">
																{t("dashboard.leadDetails.agent")}
															</Typography>
															{agentNameLoading ? (
																<CircularProgress size={16} />
															) : (
																<Typography variant="body2" sx={{ fontWeight: 500 }}>
																	{agentName}
																</Typography>
															)}
														</Box>
													</Box>
												</Box>
											)}
										</Box>

										<Box>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												<CalendarToday sx={{ fontSize: 18, color: "text.secondary" }} />
												<Box>
													<Typography variant="caption" color="text.secondary">
														{t("dashboard.leadDetails.creationDate")}
													</Typography>
													<Typography variant="body2" sx={{ fontWeight: 500 }}>
														{createdDate}
													</Typography>
												</Box>
											</Box>
										</Box>
									</Box>
								</CardContent>
							</Card>
						</Box>

						{/* Controles */}
						<Box sx={{ flex: { xs: 1, md: 1 }, minWidth: "280px" }}>
							{displayLead.assignedTo ? (
								<Card sx={{ border: `1px solid ${alpha(statusColor, 0.2)}` }}>
									<CardContent sx={{ p: 2 }}>
										<Typography
											variant="subtitle1"
											sx={{ fontWeight: 600, color: statusColor, mb: 2 }}
										>
											{t("dashboard.leadDetails.settings")}
										</Typography>
										<Stack spacing={2}>
											<FormControl size="small" fullWidth>
												<InputLabel>{t("dashboard.quality")}</InputLabel>
												<Select
													value={leadQuality}
													label={t("dashboard.quality")}
													onChange={(e) => setLeadQuality(e.target.value as LeadQuality)}
												>
													<MenuItem value="Frio">❄️ {t("dashboard.cold")}</MenuItem>
													<MenuItem value="Neutro">😐 {t("dashboard.neutral")}</MenuItem>
													<MenuItem value="Quente">🔥 {t("dashboard.hot")}</MenuItem>
												</Select>
											</FormControl>
											<FormControl size="small" fullWidth>
												<InputLabel>{t("dashboard.status")}</InputLabel>
												<Select
													value={status}
													label={t("dashboard.status")}
													onChange={(e) => setStatus(e.target.value as string)}
												>
													{statuses.map((statusItem) => (
														<MenuItem key={statusItem.id} value={statusItem.id}>
															<Box
																sx={{
																	display: "flex",
																	alignItems: "center",
																	gap: 1,
																	width: "100%",
																}}
															>
																<Box
																	sx={{
																		width: 12,
																		height: 12,
																		borderRadius: "50%",
																		backgroundColor: statusItem.color,
																		flexShrink: 0,
																	}}
																/>
																<Typography variant="body2" sx={{ flex: 1 }}>
																	{statusItem.name}
																</Typography>
															</Box>
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Stack>
									</CardContent>
								</Card>
							) : (
								<Card sx={{ border: `1px solid ${alpha(statusColor, 0.2)}` }}>
									<CardContent sx={{ p: 2, textAlign: "center" }}>
										<Typography variant="body2" color="text.secondary">
											{t("dashboard.leadDetails.unassignedLead")}
										</Typography>
									</CardContent>
								</Card>
							)}
						</Box>
					</Box>

					{/* Seções Expansíveis */}
					<Box sx={{ mt: 2 }}>
						<Stack spacing={1}>
							{/* Resumo da IA */}
							{displayLead.resumeAI && (
								<Accordion>
									<AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: "48px" }}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<AutoAwesome sx={{ fontSize: 20, color: statusColor }} />
											<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
												{t("dashboard.leadDetails.aiSummary")}
											</Typography>
										</Box>
									</AccordionSummary>
									<AccordionDetails sx={{ pt: 0 }}>
										<Typography
											variant="body2"
											sx={{
												lineHeight: 1.8,
												whiteSpace: "pre-wrap",
												wordBreak: "break-word",
												color: "text.secondary",
											}}
										>
											{displayLead.resumeAI}
										</Typography>
									</AccordionDetails>
								</Accordion>
							)}

							{/* Lead Ratings */}
							{displayLead.rating && (
								<Accordion>
									<AccordionSummary
										expandIcon={<ExpandMore />}
										sx={{ minHeight: "48px" }}
										onClick={() => setExpandRatings(!expandRatings)}
									>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<HotelClass sx={{ fontSize: 20, color: statusColor }} />
											<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
												{t("dashboard.leadDetails.ratingsSummary")}
											</Typography>
											{
												[...new Array(displayLead.rating?.businessPotencial || 0)].map((_, index) => (
													<Star key={index} sx={{ fontSize: 20, color: yellow[500] }} />
												))
											}
										</Box>
									</AccordionSummary>
									<AccordionDetails sx={{ pt: 0 }}>
										<Grid container spacing={2}>
											<RatingGridItem
												title={t("dashboard.leadDetails.ratings.age")}
												value={displayLead.rating?.age?.toString() || "N/A"}
											/>
											<RatingGridItem
												title={t("dashboard.leadDetails.ratings.genre")}
												value={displayLead.rating?.gender || "N/A"}
											/>
											<RatingGridItem
												title={t("dashboard.leadDetails.ratings.type")}
												value={getDocumentType(displayLead.rating?.document || "") || "N/A"}
											/>
											<RatingGridItem
												title={t("dashboard.leadDetails.ratings.document")}
												value={displayLead.rating?.document || "N/A"}
											/>
											<RatingGridItem
												title={t("dashboard.leadDetails.ratings.creditScore")}
												value={displayLead.rating?.creditScoreD00 || "N/A"}
											/>
											<Grid size={12}>
												<Typography sx={{ fontWeight: 600, fontSize: "1.2rem" }}>
													{t("dashboard.leadDetails.ratings.income")}
												</Typography>
											</Grid>
											<RatingGridItem
												title={t(
													`dashboard.leadDetails.ratings.${getDocumentType(displayLead.rating?.document || "") === "Pessoa Física"
														? "personalIncome"
														: "companyShared"
													}`
												)}
												value={
													getDocumentType(displayLead.rating?.document || "") === "Pessoa Física"
														? displayLead.rating?.incomePersonal || "N/A"
														: displayLead.rating?.revenueShared || "N/A"
												}
											/>
											<RatingGridItem
												title={t(
													`dashboard.leadDetails.ratings.${getDocumentType(displayLead.rating?.document || "") === "Pessoa Física"
														? "partnerIncome"
														: "companyBalance"
													}`
												)}
												value={
													getDocumentType(displayLead.rating?.document || "") === "Pessoa Física"
														? displayLead.rating?.incomePartner || "N/A"
														: displayLead.rating?.revenueBalance || "N/A"
												}
											/>
											<RatingGridItem
												title={t(
													`dashboard.leadDetails.ratings.${getDocumentType(displayLead.rating?.document || "") === "Pessoa Física"
														? "familyIncome"
														: "companyPresumed"
													}`
												)}
												value={
													getDocumentType(displayLead.rating?.document || "") === "Pessoa Física"
														? displayLead.rating?.incomeFamily || "N/A"
														: displayLead.rating?.revenuePresumed || "N/A"
												}
											/>
										</Grid>
									</AccordionDetails>
								</Accordion>
							)}

							{/* Observações */}
							{lead.assignedTo && (
								<Accordion>
									<AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: "48px" }}>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
												width: "100%",
												justifyContent: "space-between",
											}}
										>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												<Comment sx={{ fontSize: 20, color: statusColor }} />
												<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
													{t("dashboard.leadDetails.observations")} ({observacoes.length})
												</Typography>
											</Box>
											<Button
												size="small"
												variant="outlined"
												onClick={(e) => {
													e.stopPropagation();
													setObservacoesModalOpen(true);
												}}
												sx={{ fontSize: "0.75rem", py: 0.5, px: 1 }}
											>
												{t("dashboard.leadDetails.manage")}
											</Button>
										</Box>
									</AccordionSummary>
									<AccordionDetails sx={{ pt: 0 }}>
										{observacoes.length === 0 ? (
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ textAlign: "center", py: 2 }}
											>
												{t("dashboard.leadDetails.noObservations")}
											</Typography>
										) : (
											<Stack spacing={1}>
												{[...observacoes]
													.sort(
														(a, b) =>
															new Date(b.dataContato).getTime() -
															new Date(a.dataContato).getTime()
													)
													.slice(0, 2)
													.map((observacao) => (
														<Paper
															key={observacao._id}
															sx={{
																p: 1.5,
																backgroundColor: alpha(statusColor, 0.05),
																border: `1px solid ${alpha(statusColor, 0.1)}`,
															}}
														>
															<Typography
																variant="caption"
																color="text.secondary"
																sx={{ display: "block", mb: 0.5 }}
															>
																{new Date(observacao.dataContato).toLocaleString(
																	"pt-BR"
																)}
															</Typography>
															<Typography variant="body2">
																{observacao.comentario &&
																	observacao.comentario.length > 80
																	? `${observacao.comentario.substring(0, 80)}...`
																	: observacao.comentario}
															</Typography>
														</Paper>
													))}
												{observacoes.length > 2 && (
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ textAlign: "center" }}
													>
														+{observacoes.length - 2}{" "}
														{t("dashboard.leadDetails.moreObservations")}
													</Typography>
												)}
											</Stack>
										)}
									</AccordionDetails>
								</Accordion>
							)}

							{/* Histórico de Conversas */}
							{conversation && conversation.messages.length > 0 && (
								<Accordion>
									<AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: "48px" }}>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
												width: "100%",
												justifyContent: "space-between",
											}}
										>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												<Chat sx={{ fontSize: 20, color: statusColor }} />
												<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
													{t("dashboard.leadDetails.conversations")} (
													{conversation.messages.length})
												</Typography>
											</Box>
											{/* {lead.assignedTo && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={isAIGenerating ? <CircularProgress size={12} /> : <AutoAwesome />}
                          onClick={(e) => {
                            e.stopPropagation();
                            generateAISuggestion();
                          }}
                          disabled={isAIGenerating}
                          sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
                        >
                          {isAIGenerating ? 'IA...' : 'Sugerir resposta da IA'}
                        </Button>
                      )} */}
										</Box>
									</AccordionSummary>
									<AccordionDetails sx={{ pt: 0 }}>
										<Box
											sx={{
												maxHeight: 450,
												padding: "20px",
												overflowY: "auto",
												background:
													theme.palette.mode === "dark"
														? "#1a1a1a"
														: "url('/whatsapp-background.jpg')",
												backgroundRepeat: "repeat",
												backgroundSize: theme.palette.mode === "dark" ? "auto" : "400px auto",
												backgroundPosition: "top left",
												borderRadius: "12px",
												border: `1px solid ${theme.palette.mode === "dark"
														? "rgba(255, 255, 255, 0.06)"
														: "transparent"
													}`,
												"&::-webkit-scrollbar": {
													width: "6px",
												},
												"&::-webkit-scrollbar-track": {
													background: alpha(statusColor, 0.1),
													borderRadius: "3px",
												},
												"&::-webkit-scrollbar-thumb": {
													background: alpha(statusColor, 0.3),
													borderRadius: "3px",
													"&:hover": {
														background: alpha(statusColor, 0.5),
													},
												},
											}}
										>
											{conversation.messages.map((message, index) => (
												<Box key={index} sx={{ mb: 2.5 }}>
													<Box
														sx={{
															display: "flex",
															justifyContent:
																message.role === "assistant"
																	? "flex-start"
																	: "flex-end",
															mb: 1,
														}}
													>
														<Paper
															sx={{
																p: 2.5,
																maxWidth: "75%",
																backgroundColor:
																	message.role === "assistant"
																		? theme.palette.mode === "dark"
																			? "rgba(33, 150, 243, 0.15)"
																			: "#e3f2fd"
																		: theme.palette.mode === "dark"
																			? "rgba(255,255,255,0.05)"
																			: "#f3f4f6",
																border: `1px solid ${message.role === "assistant"
																		? theme.palette.mode === "dark"
																			? "rgba(33, 150, 243, 0.3)"
																			: "#2196f3"
																		: statusColor
																	}`,
																borderRadius:
																	message.role === "assistant"
																		? "20px 20px 20px 5px"
																		: "20px 20px 5px 20px",
																boxShadow: 1,
															}}
														>
															<Box
																sx={{ display: "flex", alignItems: "center", mb: 1.5 }}
															>
																{message.role === "assistant" ? (
																	<AutoAwesome
																		sx={{
																			mr: 1.5,
																			fontSize: 18,
																			color:
																				theme.palette.mode === "dark"
																					? "#65f0c5"
																					: "#2196f3",
																		}}
																	/>
																) : (
																	<AccountCircle
																		sx={{
																			mr: 1.5,
																			fontSize: 18,
																			color: statusColor,
																		}}
																	/>
																)}
																<Typography
																	variant="caption"
																	sx={{
																		fontWeight: "bold",
																		color:
																			message.role === "assistant"
																				? theme.palette.mode === "dark"
																					? "#65f0c5"
																					: "#2196f3"
																				: statusColor,
																	}}
																>
																	{message.role === "assistant"
																		? `🤖 ${t("dashboard.leadDetails.aiAssistant")}`
																		: `👤 ${displayLead.name}`}
																</Typography>
															</Box>
															<Typography
																variant="body1"
																sx={{
																	whiteSpace: "pre-wrap",
																	lineHeight: 1.6,
																	fontSize: "0.95rem",
																	color:
																		theme.palette.mode === "dark"
																			? message.role === "assistant"
																				? "#e2e8f0"
																				: "#f8fafc"
																			: "inherit",
																	wordBreak: "break-word",
																	overflowWrap: "break-word",
																	hyphens: "auto",
																	maxWidth: "100%",
																}}
															>
																{message.content}
															</Typography>
															<Typography
																variant="caption"
																color="text.secondary"
																sx={{
																	mt: 1.5,
																	display: "block",
																	fontStyle: "italic",
																	color:
																		theme.palette.mode === "dark"
																			? message.role === "assistant"
																				? "#94a3b8"
																				: "#cbd5e1"
																			: "inherit",
																}}
															>
																{new Date(message.timestamp.$date).toLocaleString(
																	"pt-BR",
																	{
																		day: "2-digit",
																		month: "2-digit",
																		year: "numeric",
																		hour: "2-digit",
																		minute: "2-digit",
																	}
																)}
															</Typography>
														</Paper>
													</Box>
												</Box>
											))}
										</Box>
									</AccordionDetails>
								</Accordion>
							)}

							{/* Sugestão da IA */}
							{showAISuggestion && aiSuggestion && lead.assignedTo && (
								<Card
									sx={{
										border:
											theme.palette.mode === "dark" ? "2px solid #a855f7" : "2px solid #9c27b0",
										background:
											theme.palette.mode === "dark"
												? "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)"
												: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
									}}
								>
									<CardContent sx={{ p: 2 }}>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												mb: 2,
											}}
										>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												<AutoAwesome
													sx={{
														color: theme.palette.mode === "dark" ? "#a855f7" : "#9c27b0",
													}}
												/>
												<Typography
													variant="subtitle1"
													sx={{
														color: theme.palette.mode === "dark" ? "#a855f7" : "#9c27b0",
														fontWeight: 600,
													}}
												>
													{t("dashboard.leadDetails.aiSuggestion")}
												</Typography>
											</Box>
											<IconButton size="small" onClick={() => setShowAISuggestion(false)}>
												<Close sx={{ fontSize: 18 }} />
											</IconButton>
										</Box>
										<Paper sx={{ p: 2, backgroundColor: "white", mb: 2 }}>
											<Typography
												variant="body2"
												sx={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}
											>
												{aiSuggestion}
											</Typography>
										</Paper>
										<Button
											variant="contained"
											startIcon={<WhatsApp />}
											onClick={() =>
												displayLead.phoneNumber &&
												aiSuggestion &&
												openWhatsAppWithMessage(displayLead.phoneNumber, aiSuggestion)
											}
											sx={{
												backgroundColor: "#25d366",
												"&:hover": { backgroundColor: "#20ba5c" },
												textTransform: "none",
											}}
											size="small"
										>
											{t("dashboard.leadDetails.sendViaWhatsApp")}
										</Button>
									</CardContent>
								</Card>
							)}
						</Stack>
					</Box>
				</DialogContent>

				<DialogActions
					sx={{
						p: 2,
						backgroundColor: "background.paper",
						borderTop: (theme) => `1px solid ${theme.palette.divider}`,
					}}
				>
					{lead.assignedTo ? (
						<Box
							sx={{
								display: "flex",
								flexDirection: { xs: "column", sm: "row" },
								gap: { xs: 1, sm: 1 },
								width: "100%",
							}}
						>
							{/* Primeira linha - Mobile: WhatsApp, Conversar, Transferir */}
							<Box
								sx={{
									display: "flex",
									gap: 1,
									width: { xs: "100%", sm: "auto" },
									justifyContent: { xs: "space-between", sm: "flex-start" },
								}}
							>
								<Button
									variant="contained"
									color="success"
									onClick={() => lead.phoneNumber && openWhatsApp(lead.phoneNumber)}
									disabled={!lead.phoneNumber}
									size="small"
									sx={{
										backgroundColor: "#25d366",
										"&:hover": { backgroundColor: "#20ba5c" },
										textTransform: "none",
										minWidth: { xs: "auto", sm: "auto" },
										px: { xs: 1, sm: 2 },
										"& .MuiButton-startIcon": {
											margin: { xs: 0, sm: "0 8px 0 -4px" },
										},
									}}
									startIcon={<WhatsApp />}
								>
									<Box sx={{ display: { xs: "none", sm: "inline" } }}>WhatsApp</Box>
								</Button>
								{conversation && conversation.messages.length > 0 && !hideConversarButton && (
									<Button
										variant="outlined"
										startIcon={<Chat />}
										onClick={handleStartConversation}
										size="small"
										sx={{
											textTransform: "none",
											flex: { xs: 1, sm: "none" },
										}}
									>
										{t("dashboard.leadDetails.chat")}
									</Button>
								)}
								{/* Botão de Transferir - apenas para admin e gerente */}
								{(isAdminLike(user?.role || "") || isManagerRole(user?.role || "")) &&
									onTransferLead && (
										<Button
											variant="outlined"
											startIcon={<CompareArrows />}
											onClick={() => onTransferLead(lead)}
											size="small"
											sx={{
												textTransform: "none",
												borderColor: "#ff9800",
												color: "#ff9800",
												flex: { xs: 1, sm: "none" },
												"&:hover": {
													borderColor: "#f57c00",
													backgroundColor: "rgba(255, 152, 0, 0.04)",
												},
											}}
										>
											{t("dashboard.leadDetails.transfer")}
										</Button>
									)}
							</Box>

							{/* Segunda linha - Mobile: Excluir, Cancelar, Salvar */}
							<Box
								sx={{
									display: "flex",
									gap: 1,
									width: { xs: "100%", sm: "auto" },
									justifyContent: { xs: "space-between", sm: "flex-end" },
									mt: { xs: 1, sm: 0 },
								}}
							>
								{onDeleteLead && (
									<Button
										variant="outlined"
										color="error"
										onClick={() => onDeleteLead(lead)}
										size="small"
										sx={{
											textTransform: "none",
											borderColor: "#d32f2f",
											color: "#d32f2f",
											minWidth: { xs: "auto", sm: "auto" },
											px: { xs: 1, sm: 2 },
											"&:hover": {
												borderColor: "#b71c1c",
												backgroundColor: "rgba(211, 47, 47, 0.04)",
											},
										}}
									>
										<Delete sx={{ display: { xs: "block", sm: "none" } }} />
										<Box
											sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 0.5 }}
										>
											<Delete />
											{t("common.delete")}
										</Box>
									</Button>
								)}
								<Button
									onClick={handleClose}
									color="inherit"
									sx={{
										ml: { xs: "auto", sm: "auto" },
										flex: { xs: 1, sm: "none" },
									}}
								>
									{t("common.cancel")}
								</Button>
								<Button
									onClick={handleSaveChanges}
									variant="contained"
									sx={{
										backgroundColor: statusColor,
										"&:hover": { backgroundColor: statusColor },
										textTransform: "none",
										flex: { xs: 1, sm: "none" },
									}}
								>
									{t("common.save")}
								</Button>
							</Box>
						</Box>
					) : (
						<Box sx={{ display: "flex", gap: 1, width: "100%" }}>
							{isSalesRole(user?.role || "") && onAssignToMe && (
								<Button
									variant="contained"
									startIcon={<Assignment />}
									onClick={() => {
										onAssignToMe(lead._id.$oid);
										setTimeout(() => handleClose(), 100);
									}}
									sx={{ textTransform: "none", flex: 1 }}
								>
									{t("dashboard.leadDetails.assignToMe")}
								</Button>
							)}
							{conversation &&
								conversation.messages.length > 0 &&
								(isSalesRole(user?.role || "") || lead.assignedTo === user?.id) && (
									<Button
										variant="outlined"
										startIcon={<Chat />}
										onClick={handleStartConversation}
										sx={{ textTransform: "none" }}
									>
										{t("dashboard.leadDetails.chat")}
									</Button>
								)}
							{onDeleteLead && (
								<Button
									variant="outlined"
									color="error"
									startIcon={<Delete />}
									onClick={() => onDeleteLead(lead)}
									sx={{
										textTransform: "none",
										borderColor: "#d32f2f",
										color: "#d32f2f",
										"&:hover": {
											borderColor: "#b71c1c",
											backgroundColor: "rgba(211, 47, 47, 0.04)",
										},
									}}
								>
									{t("common.delete")}
								</Button>
							)}
							<Button onClick={handleClose} color="inherit">
								{t("dashboard.leadDetails.close")}
							</Button>
						</Box>
					)}
				</DialogActions>
			</Dialog>

			{lead.assignedTo && (
				<ObservacoesModal
					open={observacoesModalOpen}
					onClose={handleCloseObservacoesModal}
					lead={lead}
					onLeadUpdate={handleLeadUpdate}
				/>
			)}
		</>
	);
};

export default LeadDetailModal;

const RatingGridItem = ({ title, value }: { title: string; value: string }) => {
	return (
		<Grid
			size={12}
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				gap: 1,
			}}
		>
			<Typography sx={{ fontWeight: 500 }}>{title}:</Typography>
			<Typography sx={{ fontWeight: 500 }}>{value}</Typography>
		</Grid>
	);
};
